import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

const ToastContext = React.createContext(null);

const TOAST_STYLE = {
  success: 'border-emerald-400/40 bg-emerald-900/85 text-emerald-100',
  info: 'border-blue-400/40 bg-slate-900/90 text-blue-100',
  error: 'border-red-400/40 bg-red-900/90 text-red-100',
};

function createToastId() {
  return `toast_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = React.useState([]);
  const timersRef = React.useRef(new Map());

  const clearTimer = React.useCallback((id) => {
    const existing = timersRef.current.get(id);
    if (existing) {
      window.clearTimeout(existing);
      timersRef.current.delete(id);
    }
  }, []);

  const dismissToast = React.useCallback((id) => {
    if (!id) return;
    clearTimer(id);
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, [clearTimer]);

  const showToast = React.useCallback((options) => {
    const {
      id,
      message,
      type = 'success',
      duration = 2800,
      persistent = false,
      spinner = false,
    } = options || {};

    if (!message) {
      return '';
    }

    const toastId = id || createToastId();

    clearTimer(toastId);

    setToasts((prev) => {
      const nextToast = {
        id: toastId,
        message,
        type,
        duration,
        persistent,
        spinner,
      };
      const existingIndex = prev.findIndex((toast) => toast.id === toastId);
      if (existingIndex >= 0) {
        const next = [...prev];
        next[existingIndex] = nextToast;
        return next;
      }
      return [nextToast, ...prev];
    });

    if (!persistent && duration > 0) {
      const timeoutId = window.setTimeout(() => {
        dismissToast(toastId);
      }, duration);
      timersRef.current.set(toastId, timeoutId);
    }

    return toastId;
  }, [clearTimer, dismissToast]);

  React.useEffect(() => {
    return () => {
      timersRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
      timersRef.current.clear();
    };
  }, []);

  const value = React.useMemo(
    () => ({
      toasts,
      showToast,
      dismissToast,
    }),
    [toasts, showToast, dismissToast]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

function ToastViewport({ toasts, onDismiss }) {
  if (!toasts.length) return null;

  return createPortal(
    <div className="pointer-events-none fixed right-4 top-4 z-[9999] flex w-[min(92vw,26rem)] flex-col gap-2">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          role="status"
          aria-live="polite"
          className={`pointer-events-auto flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium shadow-lg shadow-black/30 backdrop-blur ${TOAST_STYLE[toast.type] || TOAST_STYLE.success}`}
        >
          {toast.spinner ? (
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent opacity-90" aria-hidden="true" />
          ) : null}
          <span className="flex-1">{toast.message}</span>
          {!toast.persistent ? (
            <button
              type="button"
              onClick={() => onDismiss(toast.id)}
              className="rounded p-0.5 opacity-80 transition hover:opacity-100"
              aria-label="Dismiss notification"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>
      ))}
    </div>,
    document.body
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
